import { useEffect, useRef, useState } from "react"
import { IsolatedScanComment, ProcessedSection, StarAPIFetchState, UserResponse, WorkflowReportSettings} from "../../types/types"
import { AngularScopeWorkflow, AngularWorkflow, PatchScopeWorkflow, Workflow } from "../../types/starAPIResponseTypes"
import { useLocation, useNavigate } from "react-router-dom"
import { downloadReport } from "../../service/scanAnalysisService"
import useScanComments from "../../hooks/useScanComments"
import useVoiceNotes from "../../hooks/useVoiceNotes"
import useAnalysisControls from "./components/AnalysisControls/useAnalysisControls"
 import { getCurrentUser } from "../../service/userService"
import { showErrorToast, showSuccessToast } from "../../utils/utils"
import useScopeWorkflowInputs from "../../hooks/useAngularScopeWorkflowInputs"
import { updateWorkflow } from "../../service/workflowService"
import useLoadingToast from "../../hooks/useLoadingToast"
import useWorkflowAnalysis from "../../hooks/useWorkflowAnalysis"
import useLoadProcessedWorkflow from "../../hooks/useLoadProcessedWorkflow"
import usePatchScopeWorkflowInputs from "../../hooks/usePatchScopeWorkflowInputs"
import WorkflowType from "../../enums/workflowType"

/**
 * UseAnalysis is responsible for managing all the state and logic for the Analysis page component.
 * 
 * @param {Function} updateToken callback function to clear token from session storage
 */
const useAnalysis = () => {

  const [{
    workflow,
    sections,
    processedSection,
    heatmapTHz,
    heatmapIR,
    heatmapMetal,
    heatmapMask,
    isolatedScans,
    images
  }] = useLoadProcessedWorkflow()

  const [{
    workflowAnalysis,
    onLoadWorkflowAnalysis,
    onAddWorkflowAnalysis,
    onEditWorkflowAnalysis
  }] = useWorkflowAnalysis()

  const [{
    filename: angularFilename,
    pipeId: angularAssetId,
    pipeLength: angularAssetLength,
    pipeTemperature: angularAssetTemperature,
    pipeDiameter: angularAssetDiameter,
    workflowStart: angularWorkflowStart,
    workflowEnd: angularWorkflowEnd,
    isScopeValidWorkflowInfo: isValidAngularWorkflow,
    setScopeWorklfowForm: setAngularWorkflowForm
  }] = useScopeWorkflowInputs({
    locations: [],
    insulationMaterials: [],
    claddingMaterials: [],
    currentSection: 0
  })

  const [{
    filename: patchFilename,
    assetId: patchAssetId,
    assetTemperature: patchAssetTemperature,
    isScopeValidWorkflowInfo: isValidPatchWorkflow,
    setScopeWorkflowForm: setPatchWorkflowForm
  }] = usePatchScopeWorkflowInputs({
    locations: [],
    insulationMaterials: [],
    claddingMaterials: [],
    currentSection: 0
  })

  const [{
    commentsTHz,
    commentsThermal,
    onLoadIsolatedScanCommentsTHz,
    onAddIsolatedScanCommentTHz,
    onDeleteTHzComment,
    onLoadIsolatedScanCommentsThermal,
    onAddIsolatedScanCommentThermal,
    onDeleteThermalComment
  }] = useScanComments()

  const [{
    voiceNoteText,
    onLoadVoiceNoteText,
    onEditVoiceNoteTranscription
  }] = useVoiceNotes()

  const [analysisControls] = useAnalysisControls({ 
    vertical: workflow.status === "success" && workflow?.data?.workflow_type === WorkflowType.PATCH
  })

  /**
   * Width of the display containers.
   */
  const [displayWidth, setDisplayWidth] = useState<number>(1)

  const [selectedSection, setSelectedSection] = useState<number>(0)

  const [selectedScan, setSelectedScan] = useState<number>(-1)

  const [selectedControl, setSelectedControl] = useState<number>(0)

  const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false)

  const [reportError, setReportError] = useState<Error | undefined>(undefined)

  const [isEditingWorkflowInfo, setIsEditingWorkflowInfo] = useState<boolean>(false)

  const [{
    onShow: showLoadingToast,
    onHide: hideLoadingToast
  }] = useLoadingToast()

  const DEFAULT_SCAN_HEIGHT = 32

  /**
   * Reference for the graph.
   */
  const dataDisplayRef = useRef<HTMLDivElement>(null)

  /**
   * Calculate width of screen
   */
  useEffect(() => {    
    const handleScreenResize = () => {
      if (dataDisplayRef === undefined || dataDisplayRef.current === undefined) { return }    
      const size = dataDisplayRef.current
    
      if (size === null || size.offsetWidth === null) { return }
      setDisplayWidth(size.offsetWidth)
    }
    handleScreenResize()
    window.addEventListener("resize", handleScreenResize)
  }, [dataDisplayRef])

  useEffect(() => {
    const workflowState = workflow
    if (workflowState.status !== "success") { return }
    const workflowData = workflowState.data
    if (!workflowData) { return }

    if (workflowData.workflow_type === WorkflowType.ANGULAR) {
      const angularWorkflowData: AngularScopeWorkflow = {
        ...workflowData,
        workflow_type: WorkflowType.ANGULAR_SCOPE,
        sections: "",
        angles: ""

      }
      const scopeWorkflow = {...angularWorkflowData, sections: "[]", angles: "[]"}
      setAngularWorkflowForm(scopeWorkflow)
    }

    if (workflowData.workflow_type === WorkflowType.PATCH) {
      const patchWorkflowData: PatchScopeWorkflow = {
        ...workflowData,
        workflow_type: WorkflowType.PATCH_SCOPE,
        cladding_material: workflowData.cladding_material ?? "",
        insulation_material: workflowData.insulation_material ?? "",
        sections: "",
        horizontal_positions: ""
      }
      const scopeWorkflow = {...patchWorkflowData, sections: "[]", horizontal_positions: "[]"}
      setPatchWorkflowForm(scopeWorkflow)
    }

    onLoadWorkflowAnalysis(workflowData.workflow_id)
  }, [workflow])

  useEffect(() => {
    const metadata = isolatedScans[0]
    if (!metadata || metadata.status !== "success") { return }
    const scanMetadata = metadata.data
    if (scanMetadata === null) { return }
    if (selectedScan !== -1) { return }

    const firstScan = scanMetadata.findIndex(heatmap => heatmap.calibration_start_p1 !== 0)
    setSelectedScan(firstScan)
  }, [isolatedScans, selectedScan])

  const onSelectSection = (section: number) => {    
    if (section > isolatedScans.length) { return }
    const metadata = isolatedScans[section]    
    if (metadata.status !== "success") { return }
    if (metadata.data === null) { return }

    setSelectedSection(section)

    const firstScan = metadata.data.findIndex( heatmap => heatmap.calibration_start_p1 !== 0)
    setSelectedScan(firstScan)
  }

  const onSelectScan = (scan: number) => {
    if (selectedSection > isolatedScans.length) { return }
    const metadata = isolatedScans[selectedSection]
    if (metadata.status !== "success") { return }
    if (metadata.data === null) { return }

    if (selectedSection > metadata.data.length) { return }
    
    if (metadata.data[scan].calibration_start_p1 === 0) { return }
    setSelectedScan(scan)
  }

  const onDownloadReport = async (reportSettings: WorkflowReportSettings) => {
    const workflowResponse = workflow
    if (workflowResponse.status !== "success") { 
      setReportError(new Error("Failed to find workflow"))
      return 
    }
    const workflowData = workflowResponse.data
    if (!workflowData) {
      setReportError(new Error("Failed to find workflow"))
      return 
    }

    setIsDownloadingReport(true)

    const res = await downloadReport(workflowData.workflow_id ?? -1, reportSettings)
    
    if (res instanceof Error) {
      setReportError(res)
      setIsDownloadingReport(false)
      return
    }

    const blob = await res.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url

    let filename = workflowData.filename
    if (filename === "") {
        filename = workflowData.asset_id
    }
        
    link.download = `${filename}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    setIsDownloadingReport(false)
  }

  /**
   * Gets wheather the area of interest checkbox should be disabled.
   * 
   * @returns true if the area of interest input should be disabled.
   */
  const disableAreaOfInterest = () => {
    if (analysisControls.selectedDisplayOption.value !== 0) { return true }
    const processedSectionState = processedSection[selectedSection]
    if (!processedSectionState) { return true }
    if (processedSectionState.status !== "success") { return true }
    const processedSectionData = processedSectionState.data

    if (processedSectionData === null) { return true }
    return !(processedSectionData.feature_vectors.length > 0)
  }

  const onEditWorkflowInfoForm = async () => {
    const workflowState = workflow
    if (workflowState.status !== "success") { return }
    const workflowData = workflowState.data
    if (!workflowData) { return }

    let updatedWorkflow = {}
    if (workflowData.workflow_type === WorkflowType.ANGULAR) {
      const isValid = isValidAngularWorkflow()
      if (!isValid) { return false }    
      showLoadingToast("Updating workflow")
      updatedWorkflow = {
        filename: angularFilename.value,
        asset_id: angularAssetId.value,
        asset_length: parseInt(angularAssetLength.value),
        asset_diameter: parseInt(angularAssetDiameter.value),
        asset_temp: parseInt(angularAssetTemperature.value),
        workflow_start: parseInt(angularWorkflowStart.value),
        workflow_end: parseInt(angularWorkflowEnd.value)
      }
      const response = await updateWorkflow(workflowData.workflow_id, updatedWorkflow)
      
      if (response instanceof Error) {
        hideLoadingToast()
        showErrorToast("Failed to update workflow.")
        return
      }
      setAngularWorkflowForm({...response, sections: "[]", angles: "[]" })
    }

    if (workflowData.workflow_type === WorkflowType.PATCH) {
      const isValid = isValidPatchWorkflow()
      if (!isValid) { return false }
      showLoadingToast("Updating workflow")
      updatedWorkflow = {
        filename: patchFilename.value,
        asset_id: patchAssetId.value,
        asset_temp: parseInt(patchAssetTemperature.value),
      }
      const response = await updateWorkflow(workflowData.workflow_id, updatedWorkflow)
      
      if (response instanceof Error) {
        hideLoadingToast()
        showErrorToast("Failed to update workflow.")
        return
      }
      setPatchWorkflowForm({...response, sections: "[]", horizontal_positions: "[]" })
    }

    setIsEditingWorkflowInfo(false)    
    hideLoadingToast()
    showSuccessToast("Workflow updated.")
  }

  const graphDataMapper = (value: number, min: number, max: number) => {
    const sample = ((max-min)/255) * value + min
    return Math.round(sample * 100) / 100
  }

  const getProcessingVersion = () => {
    const processedSectionState = processedSection[selectedSection]
    if (!processedSectionState) { return undefined }
    if (processedSectionState.status !== "success") { return undefined }
    return processedSectionState.data?.processing_version
  }

  return [{
    workflow,
    isolatedScans,
    dataDisplayOptions: [
      {
        title: "THz",
        sectionHeatmap: {
          heatmaps: heatmapTHz[selectedSection],
          isolatedScans: isolatedScans[selectedSection],
          heatmapMask: heatmapMask[selectedSection],
          width: displayWidth,
          selectedScan: selectedScan,
          highlightMin: analysisControls.minHighlightCheckbox.value,
          highlightMax: analysisControls.maxHighlightCheckbox.value,
          onSelectScan: onSelectScan,
          scanPixelHeight: analysisControls.selectedTHzScanHeight,
          colourPalette: "magma",
          showFeatureVectors: analysisControls.overlayAnalysisCheckbox.value,
        },
        graphDataKeys: ["sensor1", "sensor2", "sensor3", "sensor4"],
        minHeatmapKey: "thz_min_range_value" as keyof ProcessedSection,
        maxHeatmapKey: "thz_max_range_value" as keyof ProcessedSection,
        showAssetBaseline: true,
        verticalHeatmap: workflow.status === "success" && workflow?.data?.workflow_type === WorkflowType.PATCH,
        graphDataMapper: (value: number) => {
          const processedSectionState = processedSection[selectedSection]
          if (processedSectionState.status !== "success") { return value }
          return graphDataMapper(
            value,
            processedSectionState.data?.thz_min_range_value ?? 0,
            processedSectionState.data?.thz_max_range_value ?? 255
          )
        },
        voiceNote: {
          voiceNote: voiceNoteText,
          onLoadVoiceNoteText: (isolatedScanId: number) => {
            if (workflow.status !== "success") { return }
            onLoadVoiceNoteText(isolatedScanId, workflow.data?.user_id ?? -1)
          },
          onEditVoiceNoteText: (text: string) => {
            const sectionMetadata = isolatedScans[selectedSection]
            if (sectionMetadata.status !== "success") { return }
            onEditVoiceNoteTranscription(text, sectionMetadata.data?.[selectedScan])
          }
        },
        scanComment: {
          title: `THz comment ${selectedScan+1}`,
          comments: commentsTHz,
          onLoad: () => {
            const sectionMetadata = isolatedScans[selectedSection]
            if (sectionMetadata.status !== "success") { return }
            const scanId = sectionMetadata.data?.[selectedScan].isolated_scan_id
            if (!scanId) { return }
            onLoadIsolatedScanCommentsTHz(scanId)
          },
          onAdd: (comment: string) => {
            const sectionMetadata = isolatedScans[selectedSection]
            if (sectionMetadata.status !== "success") { return }
            const scanId = sectionMetadata.data?.[selectedScan].isolated_scan_id
            if (!scanId) { return }
            onAddIsolatedScanCommentTHz(comment, scanId)
          },
          onDelete: (comment: IsolatedScanComment) => {
            const sectionMetadata = isolatedScans[selectedSection]
            if (sectionMetadata.status !== "success") { return }
            const scanId = sectionMetadata.data?.[selectedScan].isolated_scan_id
            if (!scanId) { return }
            onDeleteTHzComment(comment, scanId)
          }
        },
        processedSection: processedSection[selectedSection],
      },
      {
        title: "IR",
        sectionHeatmap: {
          heatmaps: heatmapIR[selectedSection],
          isolatedScans: isolatedScans[selectedSection],
          heatmapMask: heatmapMask[selectedSection],
          width: displayWidth,
          selectedScan: selectedScan,
          highlightMin: analysisControls.minHighlightCheckbox.value,
          highlightMax: analysisControls.maxHighlightCheckbox.value,
          onSelectScan: onSelectScan,
          scanPixelHeight: analysisControls.selectedIRScanHeight,
          colourPalette: "viridis",
          showFeatureVectors: false,
        },
        graphDataKeys: ["sensor1", "sensor2"],
        minHeatmapKey: "ir_min_range_value" as keyof ProcessedSection,
        maxHeatmapKey: "ir_max_range_value" as keyof ProcessedSection,
        showAssetBaseline: true,
        verticalHeatmap: workflow.status === "success" && workflow?.data?.workflow_type === WorkflowType.PATCH,
        graphDataMapper: (value: number) => {
          const processedSectionState = processedSection[selectedSection]
          if (processedSectionState.status !== "success") { return value }
          return graphDataMapper(
            value,
            processedSectionState.data?.ir_min_range_value ?? 0,
            processedSectionState.data?.ir_max_range_value ?? 255
          )
        },
        voiceNote: {
          voiceNote: voiceNoteText,
          onLoadVoiceNoteText: (isolatedScanId: number) => {
            if (workflow.status !== "success") { return }
            onLoadVoiceNoteText(isolatedScanId, workflow.data?.user_id ?? -1)
          },
          onEditVoiceNoteText: (text: string) => {
            const sectionMetadata = isolatedScans[selectedSection]
            if (sectionMetadata.status !== "success") { return }
            onEditVoiceNoteTranscription(text, sectionMetadata.data?.[selectedScan])
          }
        },
        scanComment: {
          title: `IR comment ${selectedScan+1}`,
          comments: commentsThermal,
          onLoad: () => {
            const sectionMetadata = isolatedScans[selectedSection]
            if (sectionMetadata.status !== "success") { return }
            const scanId = sectionMetadata.data?.[selectedScan].isolated_scan_id
            if (!scanId) { return }
            onLoadIsolatedScanCommentsThermal(scanId)
          },
          onAdd: (comment: string) => {
            const sectionMetadata = isolatedScans[selectedSection]
            if (sectionMetadata.status !== "success") { return }
            const scanId = sectionMetadata.data?.[selectedScan].isolated_scan_id
            if (!scanId) { return }
            onAddIsolatedScanCommentThermal(comment, scanId)
          },
          onDelete: (comment: IsolatedScanComment) => {
            const sectionMetadata = isolatedScans[selectedSection]
            if (sectionMetadata.status !== "success") { return }
            const scanId = sectionMetadata.data?.[selectedScan].isolated_scan_id
            if (!scanId) { return }
            onDeleteThermalComment(comment, scanId)
          }
        },
        processedSection: processedSection[selectedSection],
        hideHeatmap: false
      },
      {
        title: "Metal",
        sectionHeatmap: {
          heatmaps: heatmapMetal[selectedSection],
          isolatedScans: isolatedScans[selectedSection],
          heatmapMask: heatmapMask[selectedSection],
          width: displayWidth,
          selectedScan: selectedScan,
          highlightMin: analysisControls.minHighlightCheckbox.value,
          highlightMax: analysisControls.maxHighlightCheckbox.value,
          onSelectScan: onSelectScan,
          scanPixelHeight: analysisControls.selectedMetalScanHeight,
          colourPalette: "bone",
          showFeatureVectors: false,
        },
        graphDataKeys: ["Detection Coil"],
        minHeatmapKey: "metal_min_range_value" as keyof ProcessedSection,
        maxHeatmapKey: "metal_max_range_value" as keyof ProcessedSection,
        showAssetBaseline: false,
        verticalHeatmap: workflow.status === "success" && workflow?.data?.workflow_type === WorkflowType.PATCH,
        graphDataMapper: (value: number) => graphDataMapper(value, 0, 1),
        voiceNote: {
          voiceNote: voiceNoteText,
          onLoadVoiceNoteText: (isolatedScanId: number) => {
            if (workflow.status !== "success") { return }
            onLoadVoiceNoteText(isolatedScanId, workflow.data?.user_id ?? -1)
          },
          onEditVoiceNoteText: (text: string) => {
            const sectionMetadata = isolatedScans[selectedSection]
            if (sectionMetadata.status !== "success") { return }
            onEditVoiceNoteTranscription(text, sectionMetadata.data?.[selectedScan])
          }
        },
        processedSection: processedSection[selectedSection],
        hideHeatmap: false
      }
    ],
    workflowOverview: {
      workflowAnalysis: workflowAnalysis,
      onEdit: (analysisId: number, comment: string, cui: boolean, mui: boolean) => {
        const workflowResponse = workflow
        if (workflowResponse.status !== "success") { return }
        const workflowData = workflowResponse.data
        if (!workflowData) { return }

        onEditWorkflowAnalysis(workflowData.workflow_id, analysisId, comment, cui, mui)
      },
      onAdd: (comment: string, cui: boolean, mui: boolean) => {
        const workflowResponse = workflow
        if (workflowResponse.status !== "success") { return }
        const workflowData = workflowResponse.data
        if (!workflowData) { return }

        onAddWorkflowAnalysis(workflowData.workflow_id, comment, cui, mui)
      }
    },
    graphRef: dataDisplayRef,
    selectedSection,
    selectedControl,
    isDownloadingReport,
    reportError,
    analysisControls,
    getProcessingVersion: () => getProcessingVersion(),
    sections,
    sectionImages: images[selectedSection],
    workflowInfoForm: {
      workflow: workflow,
      angularFilename,
      angularAssetId,
      angularAssetLength,
      angularAssetDiameter,
      angularAssetTemperature,
      angularWorkflowStart,
      angularWorkflowEnd,

      patchFilename,
      patchAssetId,
      patchAssetTemperature,
      sections,
      selectedSection: selectedSection,
      isEditing: isEditingWorkflowInfo,
      onEdit: () => setIsEditingWorkflowInfo(true),
      onCancelEdit: () => setIsEditingWorkflowInfo(false),
      onSubmit: onEditWorkflowInfoForm
    },
    
    onHideReportError: () => setReportError(undefined),
    onSelectControl: setSelectedControl,
    onSelectSection: onSelectSection,
    onDownloadReport,
    disableAreaOfInterest,
    onBack: () => window.close()
  }]
}

export default useAnalysis
